import React, { useEffect, useState } from "react";
import { Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreDispatch, useStoreSelector } from "../../../../../hooks/StoreHooks";
import { getCurrentUser, isStepVisible } from "../../../../core/common_functions";
import { useDoorConfigurationBuilderActions } from "../../../../../store/reducers/door_configuration_builder";
import { useDialogs } from "../../../../providers/dialogs_provider";
import { useOverlapConfigurationsService } from "../../../../services/overlap_configurations_service";
import { Accessorio, HandleConfiguration, HandleOptions, TipoManiglia } from "../../../../models/door_configuration";
import Loading from "../../../../../components/Loading/loading";
import PortaOverbas from "../component/door-compont-3d/PortaOverbas";
import { NullableNumber, NullableString } from "../../../../core/common_types";
import { useFinishes } from "../../../../providers/finish_provider";
import { isVerniciaturaRalAScelta } from "../component/door-compont-3d/elementi/constants";

const TIPO_MANIGLIA_OPTIONS: { value: TipoManiglia; label: string }[] = [
    { value: "DOPPIA", label: "Doppia maniglia" },
    { value: "SBLOCCO", label: "Sblocco manuale" },
];

const getNomeVerniciatura = (verniciature: Accessorio[], id: NullableNumber): NullableString =>
    verniciature.find(v => v?.id === id)?.name ?? null;

export const ManiglierePage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { base, doorConfigurationBuilder } = useStoreSelector(store => store);
    const dispatcher = useStoreDispatch();
    const currentUser = getCurrentUser(base);
    const { setStepActive, setStepComplete, setManiglieria, setVerniciatureManiglia } = useDoorConfigurationBuilderActions();
    const dialogs = useDialogs();
    const finishesProvider = useFinishes();
    const configurationService = useOverlapConfigurationsService();

    const [isLoading, setLoading] = useState(true);
    const [isSubmit, setSubmit] = useState(false);
    const [options, setOptions] = useState<HandleOptions>({ verniciature: [], sblocco: null });
    const [tipoManiglia, setTipoManiglia] = useState<TipoManiglia | null>(null);
    const [verniciatura, setVerniciatura] = useState<NullableNumber>(null);
    const [verniciaturaRal, setVerniciaturaRal] = useState<NullableString>(null);

    const load = async () => {
        try {
            if (!doorConfigurationBuilder.configuration) throw "invalid configuration";

            const opts = await configurationService.findOpzioniManigliere(currentUser, doorConfigurationBuilder.configuration.configurationId);
            const current = await configurationService.getManigliere(currentUser, doorConfigurationBuilder.configuration.configurationId);

            setOptions(opts);
            dispatcher(setVerniciatureManiglia(opts.verniciature));

            if (current) {
                setTipoManiglia(current.tipoManiglia);
                setVerniciatura(current.verniciatura);
                setVerniciaturaRal(current.verniciaturaRal ?? null);
                dispatcher(setManiglieria(current));
            }

            setLoading(false);
        } catch (e: any) {
            setLoading(false);
            if (typeof e == "string") {
                dialogs?.showError("Attenzione...", e);
            } else {
                if (e.severity == "error") {
                    dialogs?.showError("Attenzione...", e.message);
                } else if (e.severity == "warning") {
                    dialogs?.showWarning("Attenzione...", e.message);
                }
            }
        }
    };

    const submit = async () => {
        setSubmit(true);
        try {
            if (!doorConfigurationBuilder.configuration) throw "invalid configuration";
            if (!tipoManiglia) throw { severity: "warning", message: "Selezionare il tipo di maniglia" };
            if (isDoppiaManiglia && !verniciatura) throw { severity: "warning", message: "Selezionare la variante della doppia maniglia" };
            if (showVerniciaturaRal && !verniciaturaRal) throw { severity: "warning", message: "Selezionare il colore RAL della maniglieria" };

            const data: HandleConfiguration = {
                tipoManiglia,
                verniciatura,
                verniciaturaRal: showVerniciaturaRal ? verniciaturaRal : null,
            };
            await configurationService.setManigliere(currentUser, doorConfigurationBuilder.configuration.configurationId, data);

            dispatcher(setStepComplete({ stepSlug: "maniglieria", isComplete: true }));
            
            if (isStepVisible("finestratura", doorConfigurationBuilder.steps)) {
                navigate(`/overbas-configurations/${id}/finestratura`);
            } else if (isStepVisible("portina-pedonale", doorConfigurationBuilder.steps)) {
                navigate(`/overbas-configurations/${id}/portina-pedonale-affiancata`);
            } else if (doorConfigurationBuilder.configuration.isMotorizzata) {
                navigate(`/overbas-configurations/${id}/motorizzazione`);
            } else {
                navigate(`/overbas-configurations/${id}/accessori-vari`);
            }
        } catch (e: any) {
            setSubmit(false);
            if (typeof e == "string") {
                dialogs?.showError("Attenzione...", e);
            } else {
                if (e.severity == "error") {
                    dialogs?.showError("Attenzione...", e.message);
                } else if (e.severity == "warning") {
                    dialogs?.showWarning("Attenzione...", e.message);
                }
            }
        }
    };

    const handleTipoManigliaChange = (value: TipoManiglia) => {
        setTipoManiglia(value);
        const verniciaturaValue = value === "SBLOCCO" ? (options.sblocco?.id ?? null) : null;
        setVerniciatura(verniciaturaValue);
        setVerniciaturaRal(null);
        dispatcher(setManiglieria({ tipoManiglia: value, verniciatura: verniciaturaValue, verniciaturaRal: null }));
    };

    const handleVerniciaturaChange = (value: NullableNumber) => {
        const nome = getNomeVerniciatura(options.verniciature, value);
        // il RAL riguarda solo la verniciatura "RAL A SCELTA": cambiando variante va azzerato
        const ral = isVerniciaturaRalAScelta(nome) ? verniciaturaRal : null;

        setVerniciatura(value);
        setVerniciaturaRal(ral);
        dispatcher(setManiglieria({ tipoManiglia, verniciatura: value, verniciaturaRal: ral }));
    };

    const handleVerniciaturaRalChange = (value: NullableString) => {
        setVerniciaturaRal(value);
        dispatcher(setManiglieria({ tipoManiglia, verniciatura, verniciaturaRal: value }));
    };

    const isDoppiaManiglia = tipoManiglia === "DOPPIA";
    const showVerniciatura = isDoppiaManiglia && options.verniciature.length > 0;
    const nomeVerniciatura = getNomeVerniciatura(options.verniciature, verniciatura);
    const showVerniciaturaRal = showVerniciatura && isVerniciaturaRalAScelta(nomeVerniciatura);

    useEffect(() => {
        if (!id || isNaN(Number(id)) || !doorConfigurationBuilder.configuration) {
            navigate("/configurations");
        } else {
            dispatcher(setStepActive({ stepSlug: "maniglieria", isActive: true }));
            load();
        }
    }, []);

    return (
        <div className="row h-full m-0">
            <div className="col-xl-7 p-0 disegn-render" style={{ position: "relative", height: "100%" }}>
                <PortaOverbas
                    tipoManiglia={tipoManiglia}
                    verniciaturaManiglia={nomeVerniciatura}
                    coloreManigliaRal={verniciaturaRal} />
            </div>
            <div className="col-xl-5 p-0 custom-scroll">
                <div className="p-4">
                    <h5 className="text-primary">Manigliere</h5>

                    {isLoading && <Loading />}

                    {!isLoading && (
                        <>
                            <FormControl fullWidth className="mt-3">
                                <InputLabel id="tipo_maniglia">Tipo maniglia</InputLabel>
                                <Select
                                    labelId="tipo_maniglia"
                                    value={tipoManiglia ?? ""}
                                    label="Tipo maniglia"
                                    onChange={(e) => handleTipoManigliaChange(e.target.value as TipoManiglia)}>
                                    {TIPO_MANIGLIA_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                    {doorConfigurationBuilder.configuration?.vanoConAltroAccesso &&
                                        <MenuItem key={"Nessuna chiusura"} value={"Nessuna chiusura"}>Nessuna maniglia</MenuItem>
                                    }
                                </Select>
                            </FormControl>

                            {showVerniciatura && (
                                <FormControl fullWidth className="mt-3">
                                    <InputLabel id="verniciatura_maniglia">Verniciatura maniglia</InputLabel>
                                    <Select
                                        labelId="verniciatura_maniglia"
                                        value={verniciatura ?? ""}
                                        label="Verniciatura maniglia"
                                        onChange={(e) => handleVerniciaturaChange(e.target.value as NullableNumber)}>
                                        {options.verniciature.map(opt => (
                                            <MenuItem key={opt?.id} value={opt?.id}>{opt?.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {showVerniciaturaRal && (
                                <TextField
                                    fullWidth
                                    focused={false}
                                    className="mt-3"
                                    label={!verniciaturaRal ? "Seleziona il colore RAL della maniglieria" : ""}
                                    name="verniciaturaRal"
                                    value={finishesProvider?.getRalName(verniciaturaRal) ?? ""}
                                    InputProps={{
                                        readOnly: true,
                                        startAdornment: verniciaturaRal && (
                                            <InputAdornment position="start">
                                                {finishesProvider?.getRalPreview(verniciaturaRal)}
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => dialogs?.selectRalColor({
                                                    onConfirm: selectedColor => handleVerniciaturaRalChange(selectedColor)
                                                })}>
                                                    <OpenInNewIcon style={{ color: "black" }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </>
                    )}

                    {!isLoading && (
                        <div className="d-flex flex-row-reverse justify-content-between mt-3">
                            <Button disabled={isSubmit} variant="contained" className="ps-3 pe-1" onClick={() => submit()}>
                                <span>Avanti</span>
                                <NavigateNextIcon />
                            </Button>

                            <Button variant="contained" className="ps-1 pe-3" color="secondary" onClick={() => {
                                navigate(`/overbas-configurations/${doorConfigurationBuilder.configuration?.configurationId}/portina-pedonale`);
                            }}>
                                <NavigateBeforeIcon />
                                <span>Indietro</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
